/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { type Url } from '../GenericLink/types/propsTypes';
import type { Callback, NextgenRouterContextInterface, ReadonlyURLSearchParams } from './types/propsType';
import { events } from './EventEmitter';

function isValidURL(url: string) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export const getLocaleUrl = (url: Url, updatedParams: any, defaultLocale: string): Url => {
  const isUrlString = typeof url === 'string';
  let localeUrl = isUrlString ? url : url.pathname ?? '';
  if (isValidURL(localeUrl)) {
    return isUrlString ? localeUrl : url;
  }
  if (localeUrl[0] !== '/') {
    localeUrl = `/${localeUrl}`;
  }
  if (
    !localeUrl.startsWith(`/${updatedParams?.locale}`) &&
    updatedParams?.locale &&
    updatedParams?.locale !== defaultLocale
  ) {
    localeUrl = `/${updatedParams?.locale}${localeUrl}`;
  }
  localeUrl = localeUrl.replace('/[locale]', '');
  return isUrlString ? localeUrl : { ...url, href: localeUrl };
};

const useRouterWithEvents = (
  routerObj: NextgenRouterContextInterface | undefined,
  defaultLocale: string,
  isNext14: boolean,
) => {
  const getQuery = (query: ReadonlyURLSearchParams | null | undefined) =>
    query && new URLSearchParams(query as unknown as URLSearchParams).toString()
      ? `?${new URLSearchParams(query as unknown as URLSearchParams).toString()}`
      : '';

  const [url, setUrl] = useState(`${routerObj?.pathname}${getQuery(routerObj?.query)}`);

  const emitStartEvent = (newUrl: string) => {
    events.emit('routeChangeStart', newUrl);
  };

  const blockRouteEvent = (callback: Function) => {
    if (events.getIsBlockRoute()) {
      events.emit('routeChangeWarn', callback);
    } else {
      callback();
    }
  };

  const handleBackBlocked = (event: PopStateEvent) => {
    event.preventDefault();
    window.history.pushState(null, document.title, window.location.href);
    events.emit('browserBackWarn');
  };

  const handlePopstate = (event: PopStateEvent) => {
    const isBackBlocked = events.getIsBackBlocked();
    if (isBackBlocked) {
      handleBackBlocked(event);
    } else {
      emitStartEvent(`${routerObj?.pathname}${getQuery(routerObj?.query)}`);
    }
  };

  useEffect(() => {
    window.addEventListener('popstate', handlePopstate);
    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, []);

  useEffect(() => {
    events.emit('routeChangeComplete');
    setUrl(`${routerObj?.pathname}${getQuery(routerObj?.query)}`);
  }, [routerObj?.pathname, routerObj?.query]);

  if (routerObj) {
    const getUrl = () => `${routerObj?.pathname}${getQuery(routerObj?.query)}`;

    const routerWithEvents = {
      ...routerObj,
      push: (path: string, options?: any) => {
        const pushCallback = () => {
          if (path !== url) {
            emitStartEvent(path);
            routerObj.push(isNext14 ? path : (getLocaleUrl(path, routerObj.params, defaultLocale) as string), options);
          }
        };
        blockRouteEvent(pushCallback);
      },
      replace: (path: string, options?: any) => {
        const replaceCallback = () => {
          if (path !== url) {
            emitStartEvent(path);
            routerObj.replace(
              isNext14 ? path : (getLocaleUrl(path, routerObj.params, defaultLocale) as string),
              options,
            );
          }
        };
        blockRouteEvent(replaceCallback);
      },
      back: () => {
        const backCallback = () => {
          emitStartEvent(getUrl());
          routerObj.back();
        };
        blockRouteEvent(backCallback);
      },
      forward: () => {
        const forwardCallback = () => {
          emitStartEvent(getUrl());
          routerObj.forward();
        };
        blockRouteEvent(forwardCallback);
      },
      refresh: () => {
        const refreshCallback = () => routerObj.refresh();
        blockRouteEvent(refreshCallback);
      },
      events: {
        on: (eventName: string, callback: Callback) => {
          events.on(eventName, callback);
        },
        off: (eventName: string, callback: Callback) => {
          events.off(eventName, callback);
        },
        emit: (eventName: string, ...args: any[]) => {
          events.emit(eventName, ...args);
        },
        setIsBlockRoute: (isBlocked: boolean) => {
          events.setIsBlockRoute(isBlocked);
        },
        setIsBackBlocked: (isBlocked: boolean) => {
          events.setIsBackBlocked(isBlocked);
        },
        getIsBlockRoute: () => events.getIsBlockRoute(),
        getIsBackBlocked: () => events.getIsBackBlocked(),
      },
    };

    return routerWithEvents;
  }

  return undefined;
};

export default useRouterWithEvents;
